import {
	Button,
	PageTitle,
	Select,
	SelectTrigger,
	SelectValue,
} from '@/components/ui';
import { useRequireB2CPermission } from '@/hooks';
import { DayWeekContainer } from './components/DayWeekContainer';
import { BadgeInfo, CircleArrowLeft, Clock, TriangleAlert } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Slot = ({ value }: { value: string }) => {
	return (
		<div className="whitespace-nowrap rounded-md border p-2 text-xs text-zinc-500">
			{value}
		</div>
	);
};

const AvailableExplanation = () => {
	const navigate = useNavigate();
	const { isLoading, canManageAvailability } = useRequireB2CPermission();

	if (isLoading || !canManageAvailability) {
		return null;
	}

	return (
		<section className="w-full space-y-6 px-4 py-6">
			<div className="space-y-6">
				<PageTitle
					text="Guia"
					// type='guide'
				/>
				<div>
					<h1 className="whitespace-nowrap text-lg font-medium text-dark">
						Como Configurar Sua Disponibilidade
					</h1>
					<p className="text-zinc-600">
						Aprenda a definir seus horários de trabalho e gerar
						slots automaticamente para agendamentos
					</p>
				</div>
			</div>
			<div className="space-y-4 rounded-md border p-4 md:max-w-lg">
				<div className="flex items-center gap-1">
					<div className="flex size-5 items-center justify-center rounded-full bg-cyan-950 text-white">
						<span className="text-sm">1</span>
					</div>
					<h5 className="text-gray-900">Selecione o dia da semana</h5>
				</div>
				<div className="flex gap-2">
					<DayWeekContainer
						className="px-4 py-2"
						isSelected
						value={1}
					/>
					<p className="text-sm text-zinc-500">
						Escolha o dia da semana para o qual você deseja
						configurar a disponibilidade.
					</p>
				</div>
			</div>
			<div className="space-y-4 rounded-md border p-4 md:max-w-lg">
				<div className="flex items-center gap-1">
					<div className="flex size-5 items-center justify-center rounded-full bg-cyan-950 text-white">
						<span className="text-sm">2</span>
					</div>
					<h5 className="text-gray-900">Defina os Intervalos</h5>
				</div>
				<div className="flex flex-col gap-2">
					<div className="flex flex-col gap-4 md:flex-row">
						<div className="w-full space-y-3">
							<div className="flex items-end gap-1 text-gray-900">
								<Clock className="size-4 pb-0.5" />
								<h5 className="text-sm leading-4">
									Horário inicial
								</h5>
							</div>
							<Select
								disabled={true}
								className="!w-full !cursor-default"
							>
								<SelectTrigger className="h-fit w-full !cursor-default">
									<SelectValue>
										<span className="text-xs text-gray-900">
											08:00
										</span>
									</SelectValue>
								</SelectTrigger>
							</Select>
						</div>
						<div className="w-full space-y-3">
							<div className="flex items-end gap-1 text-gray-900">
								<Clock className="size-4 pb-0.5" />
								<h5 className="text-sm leading-4">
									Horário Final
								</h5>
							</div>
							<Select
								disabled={true}
								className="!w-full !cursor-default"
							>
								<SelectTrigger className="h-fit w-full !cursor-default">
									<SelectValue>
										<span className="text-xs text-gray-900">
											12:00
										</span>
									</SelectValue>
								</SelectTrigger>
							</Select>
						</div>
					</div>
					<p className="text-sm text-zinc-500">
						Escolha o início e o final da sua disponibilidade.
					</p>
				</div>
			</div>
			<div className="space-y-4 rounded-md border p-4 md:max-w-lg">
				<div className="flex items-center gap-1">
					<div className="flex size-5 items-center justify-center rounded-full bg-cyan-950 text-white">
						<span className="text-sm">3</span>
					</div>
					<h5 className="text-gray-900">
						Horários gerados automaticamente para o agendamento
					</h5>
				</div>
				<div className="space-y-2">
					<div className="flex gap-1">
						<Slot value="08:00 - 08:50" />
						<Slot value="09:00 - 09:50" />
						<Slot value="10:00 - 10:50" />
						<Slot value="11:00 - 11:50" />
					</div>
					<p className="text-sm text-zinc-500">
						Horários disponíveis na visão dos usuários.
					</p>
					<div className="space-y-1 rounded-md bg-zinc-200/30 p-2">
						<p className="text-xs leading-5 text-gray-950">
							Obs: Escolher novamente o dia da semana
							segunda-feira e intervalo das 14:00 horas há 18:00
							horas completaria um horário "padrão" com intervalo
							de almoço.
						</p>
						<div className="flex gap-1">
							<Slot value="14:00 - 14:50" />
							<Slot value="15:00 - 15:50" />
							<Slot value="16:00 - 16:50" />
							<Slot value="17:00 - 17:50" />
						</div>
					</div>
				</div>
			</div>
			<div className="space-y-2 rounded-md bg-zinc-200/50 p-4 md:max-w-lg">
				<div className="flex items-end gap-1">
					<BadgeInfo className="size-4 text-gray-900" />
					<h5 className="text-sm font-medium leading-3 text-gray-900">
						Dicas Importantes
					</h5>
				</div>
				<ul className="list-disc space-y-1 pl-5 text-sm text-zinc-600">
					<li>Cada sessão tem duração de 50 minutos.</li>
					<li>
						Há um intervalo automático de 10 minutos entre sessões.
					</li>
					<li>
						Você pode adicionar múltiplos intervalos no mesmo dia.
					</li>
					<li>O sistema previne sobreposições de horários.</li>
					<li>
						Você pode editar ou remover intervalos a qualquer
						momento.
					</li>
				</ul>
			</div>
			<div className="space-y-2 rounded-md bg-zinc-200/50 p-4 md:max-w-lg">
				<div className="flex items-end gap-1">
					<TriangleAlert className="size-4 text-gray-900" />
					<h5 className="text-sm font-medium leading-3 text-gray-900">
						Validações Automáticas
					</h5>
				</div>
				<ul className="list-disc space-y-1 pl-5 text-sm text-zinc-600">
					<li>O sistema não permite horários sobrepostos.</li>
					<li>
						Horário de início deve ser anterior ao horário de fim.
					</li>
					<li>
						{' '}
						Intervalos devem ter pelo menos 50 minutos de duração.
					</li>
				</ul>
			</div>
			<Button
				startIcon={<CircleArrowLeft className="size-4 text-dark" />}
				variant="text"
				text="voltar"
				className="w-fit justify-center text-base"
				onClick={() => navigate(-1)}
			/>
		</section>
	);
};

export { AvailableExplanation };
